import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '../../lib/supabase/client';
import DashboardLayout from '../components/layout/DashboardLayout';
import { FileText, Plus, Edit, DollarSign, Calendar, Trash2, Download } from 'lucide-react';
import { generateQuotePDF } from '../lib/pdfGenerator';
import '../components/styles.css';

export default function Quotes() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null);
  const [formData, setFormData] = useState({
    quote_number: '',
    client_id: '',
    prospect_id: '',
    title: '',
    description: '',
    status: 'draft',
    total_amount: '',
    tax_rate: '20.00',
    valid_until: '',
    notes: ''
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const supabase = createClient();
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        if (!currentUser) {
          navigate('/crm/login');
          return;
        }

        setUser(currentUser);
      } catch (error) {
        console.error('Erreur d\'authentification:', error);
        navigate('/crm/login');
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchQuotes();
      fetchClients();
      fetchProspects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filterStatus]);

  const fetchClients = async () => {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('clients')
        .select('id, last_name, company_name, client_type')
        .eq('status', 'actif')
        .order('last_name', { ascending: true });
      setClients(data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchProspects = async () => {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('prospects')
        .select('id, first_name, last_name')
        .eq('status', 'valide')
        .order('last_name', { ascending: true });
      setProspects(data || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const supabase = createClient();
      
      // Récupérer les devis
      let query = supabase
        .from('quotes')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data: quotesData, error: quotesError } = await query;

      if (quotesError) {
        throw quotesError;
      }

      // Récupérer les clients associés
      const clientIds = [...new Set((quotesData || []).map(q => q.client_id).filter(Boolean))];
      let clientsData = {};
      
      if (clientIds.length > 0) {
        const { data: clients, error: clientsError } = await supabase
          .from('clients')
          .select('id, last_name, company_name, client_type')
          .in('id', clientIds);

        if (!clientsError && clients) {
          clients.forEach(client => {
            clientsData[client.id] = client;
          });
        }
      }

      // Récupérer les prospects associés
      const prospectIds = [...new Set((quotesData || []).map(q => q.prospect_id).filter(Boolean))];
      let prospectsData = {};
      
      if (prospectIds.length > 0) {
        const { data: prospects, error: prospectsError } = await supabase
          .from('prospects')
          .select('id, first_name, last_name')
          .in('id', prospectIds);

        if (!prospectsError && prospects) {
          prospects.forEach(prospect => {
            prospectsData[prospect.id] = prospect;
          });
        }
      }

      // Joindre les données
      const quotesWithRelations = (quotesData || []).map(quote => ({
        ...quote,
        clients: quote.client_id ? clientsData[quote.client_id] : null,
        prospects: quote.prospect_id ? prospectsData[quote.prospect_id] : null
      }));

      setQuotes(quotesWithRelations);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la récupération des devis: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const generateQuoteNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `DEV-${year}${month}-${random}`;
  };

  const calculateTotals = (amount, taxRate) => {
    const total = parseFloat(amount) || 0;
    const rate = parseFloat(taxRate) || 0;
    const tax = (total * rate) / 100;
    const totalWithTax = total + tax;
    return { tax, totalWithTax };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const supabase = createClient();

    const { tax, totalWithTax } = calculateTotals(formData.total_amount, formData.tax_rate);

    const quoteData = {
      ...formData,
      quote_number: formData.quote_number || generateQuoteNumber(),
      total_amount: parseFloat(formData.total_amount) || 0,
      tax_rate: parseFloat(formData.tax_rate) || 20,
      tax_amount: tax,
      total_with_tax: totalWithTax,
      client_id: formData.client_id || null,
      prospect_id: formData.prospect_id || null,
      valid_until: formData.valid_until || null
    };

    if (editingQuote) {
      const { error } = await supabase
        .from('quotes')
        .update(quoteData)
        .eq('id', editingQuote.id);

      if (error) {
        console.error('Erreur lors de la mise à jour:', error);
        alert('Erreur lors de la mise à jour du devis');
      } else {
        setShowForm(false);
        setEditingQuote(null);
        resetForm();
        fetchQuotes();
      }
    } else {
      const { error } = await supabase
        .from('quotes')
        .insert([quoteData]);

      if (error) {
        console.error('Erreur lors de la création:', error);
        alert('Erreur lors de la création du devis');
      } else {
        setShowForm(false);
        resetForm();
        fetchQuotes();
      }
    }
  };

  const handleEdit = (quote) => {
    setEditingQuote(quote);
    setFormData({
      quote_number: quote.quote_number || '',
      client_id: quote.client_id || '',
      prospect_id: quote.prospect_id || '',
      title: quote.title || '',
      description: quote.description || '',
      status: quote.status || 'draft',
      total_amount: quote.total_amount || '',
      tax_rate: quote.tax_rate ? String(quote.tax_rate) : '20.00',
      valid_until: quote.valid_until || '',
      notes: quote.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ? Cette action est irréversible.')) {
      return;
    }

    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('quotes')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du devis');
      } else {
        fetchQuotes();
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression du devis');
    }
  };

  const resetForm = () => {
    setFormData({
      quote_number: '',
      client_id: '',
      prospect_id: '',
      title: '',
      description: '',
      status: 'draft',
      total_amount: '',
      tax_rate: '20.00',
      valid_until: '',
      notes: ''
    });
    setEditingQuote(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Brouillon';
      case 'sent': return 'Envoyé';
      case 'accepted': return 'Accepté';
      case 'rejected': return 'Refusé';
      case 'expired': return 'Expiré';
      default: return status;
    }
  };

  const getRecipientName = (quote) => {
    if (quote.clients) {
      return quote.clients.client_type === 'entreprise' 
        ? quote.clients.company_name 
        : quote.clients.last_name;
    }
    if (quote.prospects) {
      return `${quote.prospects.first_name} ${quote.prospects.last_name}`;
    }
    return 'Non assigné';
  };

  const handleDownloadPDF = async (quote) => {
    try {
      await generateQuotePDF(quote, quote.clients, quote.prospects);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-8">Chargement des devis...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Devis</h1>
            <p className="mt-2 text-gray-600">Gérez vos devis et estimations</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setFormData({ ...formData, quote_number: generateQuoteNumber() });
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Nouveau devis
          </button>
        </div>

        <div className="flex space-x-4 mb-6">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="all">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="sent">Envoyé</option>
            <option value="accepted">Accepté</option>
            <option value="rejected">Refusé</option>
            <option value="expired">Expiré</option>
          </select>
        </div>

        {showForm && (
          <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              {editingQuote ? 'Modifier le devis' : 'Nouveau devis'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de devis *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.quote_number}
                    onChange={(e) => setFormData({ ...formData, quote_number: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut *
                  </label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="draft">Brouillon</option>
                    <option value="sent">Envoyé</option>
                    <option value="accepted">Accepté</option>
                    <option value="rejected">Refusé</option>
                    <option value="expired">Expiré</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client
                  </label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => {
                      setFormData({ ...formData, client_id: e.target.value, prospect_id: '' });
                    }}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.client_type === 'entreprise' ? client.company_name : client.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prospect
                  </label>
                  <select
                    value={formData.prospect_id}
                    onChange={(e) => {
                      setFormData({ ...formData, prospect_id: e.target.value, client_id: '' });
                    }}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Sélectionner un prospect</option>
                    {prospects.map((prospect) => (
                      <option key={prospect.id} value={prospect.id}>
                        {prospect.first_name} {prospect.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Montant HT (€) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Taux de TVA (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.tax_rate}
                    onChange={(e) => setFormData({ ...formData, tax_rate: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valide jusqu'au
                  </label>
                  <input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  className="w-full p-2 border rounded-md"
                />
              </div>

              {formData.total_amount && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <p className="text-sm text-gray-600">
                    Montant HT: {parseFloat(formData.total_amount || 0).toFixed(2)}€
                  </p>
                  <p className="text-sm text-gray-600">
                    TVA ({formData.tax_rate}%): {calculateTotals(formData.total_amount, formData.tax_rate).tax.toFixed(2)}€
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    Montant TTC: {calculateTotals(formData.total_amount, formData.tax_rate).totalWithTax.toFixed(2)}€
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingQuote ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {quotes.length === 0 ? (
              <li className="p-6 text-center text-gray-500">Aucun devis trouvé.</li>
            ) : (
              quotes.map((quote) => (
                <li key={quote.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <FileText className="h-5 w-5 text-primary-600" />
                        <span className="text-lg font-semibold text-gray-900">
                          {quote.quote_number || 'Sans numéro'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                          {getStatusLabel(quote.status)}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p className="font-medium text-gray-900">{quote.title}</p>
                        <p>Destinataire: <span className="font-medium">{getRecipientName(quote)}</span></p>
                        {quote.description && <p>{quote.description}</p>}
                        <p className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          <span className="font-semibold text-primary-600">
                            {quote.total_with_tax?.toFixed(2) || '0.00'}€ TTC
                          </span>
                          {quote.total_amount && (
                            <span className="text-gray-500">
                              ({quote.total_amount.toFixed(2)}€ HT)
                            </span>
                          )}
                        </p>
                        {quote.valid_until && (
                          <p className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Valide jusqu'au {new Date(quote.valid_until).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                        {quote.notes && (
                          <p className="text-gray-700 mt-2">{quote.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end space-y-2 ml-4">
                      <span className="text-xs text-gray-500">
                        {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleDownloadPDF(quote)}
                          className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-primary-700 bg-primary-100 rounded-md hover:bg-primary-200"
                          title="Télécharger en PDF"
                        >
                          <Download className="h-4 w-4" />
                          PDF
                        </button>
                        <button
                          onClick={() => handleEdit(quote)}
                          className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          <Edit className="h-4 w-4" />
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(quote.id)}
                          className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
                        >
                          <Trash2 className="h-4 w-4" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
